import React from "react";
import Layout from "../../components/Layout/Layout";

const ScamPrevention = () => {
    return (
        <Layout>
            <section className="page-hero">
                <div className="container">
                    <div className="eyebrow">
                        <span className="dot"></span> Scam Prevention
                    </div>
                    <h1>Scam Prevention</h1>
                    <p>
                        Everest Network Limited (“Everest”, “we”, “us”) is working hard to fight cybercrime and fraud, to keep you
                        as safe as possible. We use transaction monitoring and screening tools — including real-time AML/CFT
                        transaction monitoring and wallet/blockchain-address screening, sanctions and politically-exposed-person
                        (PEP) screening, and real-time KYC/KYB re-verification and risk-rating checks via our EverID identity
                        system — to identify and prevent fraud and money laundering.
                    </p>
                    <p>Below are answers to a few common questions about fraud and how you can prevent it:</p>
                </div>
            </section>

            <section className="section section-tight">
                <div className="container grid-2">
                    <article className="card" style={{ gridColumn: "1 / -1" }}>
                        <span className="kicker">Regulatory status</span>
                        <h2>How do I verify who I’m dealing with?</h2>
                        <p>
                            Everest provides crypto-asset services including the execution of orders, placing of crypto-assets,
                            custody and administration, transfer services, and reception and transmission of orders as authorised
                            by the Malta Financial Services Authority (MFSA) as a Crypto-Asset Service Provider (CASP), under the
                            Markets in Crypto-Assets Regulation (MiCA). You can verify this by searching “Everest Network Ltd” on
                            the MFSA’s Financial Services Register and on ESMA’s Interim MiCA Register of CASPs.
                        </p>
                        <p>
                            Card, open banking, and e-money wallet functionality connected to your Everest account is provided by
                            a regulated and licensed Financial Institution, which holds client fiat funds. Everest does not itself
                            hold client fiat funds.
                        </p>
                        <p>
                            If someone contacts you claiming to represent Everest, our payment partner, or any related entity, and
                            you’re unsure whether the communication is genuine, don’t act on it. Contact us directly first using
                            the verified details on this page.
                        </p>
                    </article>

                    <article className="card">
                        <span className="kicker">The basics</span>
                        <h2>What is a scam?</h2>
                        <p>
                            A scam is where you’re tricked into sending money or digital assets (i.e. cryptocurrencies) to a
                            scammer, who then steals the money or digital assets. Scammers might impersonate people and
                            organisations you trust, using documents, emails, phone calls, and texts that seem genuine. They may
                            ask you to download apps or sign up for specialised investment websites. Many victims are contacted by
                            scammers through investment, trading or chat groups on WhatsApp, Telegram or Signal. Many scams promise
                            overly high investment returns or claim to facilitate access to investment opportunities not available
                            to the general public.
                        </p>
                    </article>

                    <article className="card">
                        <span className="kicker">Reporting a scam</span>
                        <h2>I’ve been scammed and the transaction details mention Everest. What should I do?</h2>
                        <p>If you believe you have been affected by fraud or a scam, act as quickly as possible:</p>
                        <ul className="bullet-list micro-list">
                            <li>
                                <strong>Fiat currency payments</strong> (e.g. bank transfers, card payments): contact your bank,
                                card issuer, or financial institution immediately. They may be able to recall or block the payment
                                — though this isn’t guaranteed, and becomes less likely the longer you wait.
                            </li>
                            <li>
                                <strong>Crypto-asset transfers:</strong> contact us immediately at{" "}
                                <a href="mailto:contact@everest.org" style={{ color: "var(--brand)" }}>
                                    contact@everest.org
                                </a>
                                . Transactions confirmed on the blockchain generally cannot be reversed, but reporting it promptly
                                helps us flag associated wallet addresses and may assist any investigation.
                            </li>
                        </ul>
                        <p>
                            You should also report any suspected fraud or scam to your local law enforcement agency, and notify us
                            directly at{" "}
                            <a href="mailto:contact@everest.org" style={{ color: "var(--brand)" }}>
                                contact@everest.org
                            </a>
                            .
                        </p>
                    </article>

                    <article className="card" style={{ gridColumn: "1 / -1" }}>
                        <span className="kicker">Verifying communications</span>
                        <h2>
                            I’ve been contacted by someone claiming to represent Everest. How do I know this communication is
                            legitimate?
                        </h2>
                        <p>
                            <strong>Check the source:</strong> Legitimate communications from Everest will only come from our
                            official email address:{" "}
                            <a href="mailto:contact@everest.org" style={{ color: "var(--brand)" }}>
                                contact@everest.org
                            </a>
                            , our client support channel (available by email and, during business hours, by telephone), or via our
                            official platforms at{" "}
                            <a href="https://www.everest.org" style={{ color: "var(--brand)" }}>
                                www.everest.org
                            </a>{" "}
                            and{" "}
                            <a
                                href="https://account.everest.org"
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: "var(--brand)" }}
                            >
                                account.everest.org
                            </a>
                            . We urge you to exercise caution and verify a sender’s identity before responding to any communication
                            claiming to be from us.
                        </p>
                        <h3 style={{ marginTop: 18 }}>We will never ask you to:</h3>
                        <ul className="bullet-list micro-list">
                            <li>Disclose your password or one-time passcodes.</li>
                            <li>Disclose full authentication credentials.</li>
                            <li>Provide confidential information over social media platforms.</li>
                            <li>Move funds to a “safe account”.</li>
                            <li>Transfer funds or assets for “verification purposes”.</li>
                            <li>Disclose your wallet’s recovery phrase (seed phrase) or private keys.</li>
                            <li>
                                Approve a wallet connection, token/spending approval, or smart-contract transaction on our behalf.
                            </li>
                        </ul>
                    </article>

                    <article className="card">
                        <span className="kicker">Warning signs</span>
                        <h2>How do I spot a scam?</h2>
                        <p>Scammers employ a range of tactics:</p>
                        <ul className="bullet-list micro-list">
                            <li>
                                <strong>Creating urgency:</strong> Pressuring you to act quickly, such as claiming an offer is
                                limited or payment is needed immediately.
                            </li>
                            <li>
                                <strong>Promising high returns:</strong> Offering unusually high or guaranteed profits, often linked
                                to cryptocurrencies, stablecoins, AI, “algorithmic trading”, or other complex or trending products.
                            </li>
                            <li>
                                <strong>Pretending to be trusted people:</strong> Impersonating banks, regulators, colleagues,
                                friends, or real company employees.
                            </li>
                            <li>
                                <strong>Building trust over time:</strong> Using social media, messaging apps, or group chats to
                                form relationships before introducing investment opportunities or asking for money.
                            </li>
                            <li>
                                <strong>Using emotional manipulation:</strong> Exploiting personal relationships (common in romance
                                scams) by fabricating emergencies, hardships, or trust-based investment opportunities to encourage
                                payments.
                            </li>
                            <li>
                                <strong>Exclusivity:</strong> Claiming opportunities are “invite-only” or available only to a select
                                few.
                            </li>
                            <li>
                                <strong>Looking professional:</strong> Using polished websites, contracts, apps, or documents to
                                appear legitimate.
                            </li>
                            <li>
                                <strong>Payment redirection:</strong> Asking you to send funds to new or different accounts (often
                                at short notice), sometimes claiming it is for security, efficiency, or compliance reasons.
                            </li>
                            <li>
                                <strong>Using complex language:</strong> Overloading you with technical jargon or complex
                                explanations (e.g. around trading strategies or crypto infrastructure) to sound credible and
                                discourage questions.
                            </li>
                            <li>
                                <strong>Asking for more money over time:</strong> Encouraging increasingly large payments over time,
                                often by showing fake “profits” or returns at first.
                            </li>
                            <li>
                                <strong>Crypto-specific red flags:</strong> Fake apps, browser extensions, or wallet-connection
                                pop-ups impersonating Everest, EverWallet, or EverID; “support” agents on Discord, Telegram, or
                                social media asking you to share your seed phrase or approve a transaction to “fix” an issue;
                                unsolicited airdrops or NFTs that prompt you to connect your wallet or sign a transaction; and
                                lookalike wallet addresses designed to be confused with one you’ve used before (“address
                                poisoning”).
                            </li>
                        </ul>
                    </article>

                    <article className="card">
                        <span className="kicker">Staying safe</span>
                        <h2>How can you protect yourself from scams?</h2>
                        <ul className="check-list micro-list">
                            <li>
                                <strong>Be cautious:</strong> Be wary of offers promising guaranteed profits or “get rich quick”
                                opportunities.
                            </li>
                            <li>
                                <strong>Don’t rush:</strong> If you feel pressured to act urgently, stop and verify the request
                                independently before proceeding.
                            </li>
                            <li>
                                <strong>Check identities:</strong> Research companies or individuals before sending any money. Look
                                for public sources and reviews, and check email addresses, phone numbers, and website domains
                                carefully for inconsistencies, misspellings, or anything suspicious.
                            </li>
                            <li>
                                <strong>Check licensing:</strong> Confirm whether a firm is authorised before sending money or
                                crypto-assets. Everest Network Limited is authorised by the MFSA as a Crypto-Asset Service Provider
                                under MiCA — you can verify this by searching “Everest Network Ltd” on the MFSA’s Financial Services
                                Register and on ESMA’s Interim MiCA Register of CASPs. Card, open banking, and e-money wallet
                                functionality connected to your Everest account is provided by a regulated and licensed Financial
                                Institution.
                            </li>
                            <li>
                                <strong>Protect personal information:</strong> Don’t share personal details or identity documents
                                with people you only know online.
                            </li>
                            <li>
                                <strong>Keep control of your accounts:</strong> Never let someone else open or manage payment
                                accounts or crypto wallets for you without proper verification.
                            </li>
                            <li>
                                <strong>Avoid remote access:</strong> Do not install remote access software that gives strangers
                                access and control over your device or financial accounts.
                            </li>
                            <li>
                                <strong>Known counterparties:</strong> Be cautious when sending money or cryptocurrencies to
                                individuals you have only met online, including through social media, messaging apps, or group
                                chats.
                            </li>
                            <li>
                                <strong>Payment requests:</strong> Be cautious of unexpected or urgent requests for money or changes
                                to payment details, especially if they involve changes to usual payment details or methods.
                            </li>
                            <li>
                                <strong>Look out for recovery scams:</strong> If you are scammed, scammers may pretend they can
                                recover lost funds or digital assets if you pay them upfront.
                            </li>
                            <li>
                                <strong>Be aware of emotional pressure:</strong> Romance, emergencies, or personal hardship may be
                                used as a way to influence your decisions.
                            </li>
                            <li>
                                <strong>Use secure communication:</strong> Don’t click on links or download attachments in
                                unexpected messages, particularly those asking for payments, personal information, or login details.
                            </li>
                            <li>
                                <strong>Ask for advice:</strong> If in doubt, speak to someone you trust or contact the organisation
                                directly using verified contact details.
                            </li>
                            <li>
                                <strong>Review transactions carefully:</strong> Double check payment details carefully before
                                sending funds, especially for first-time or high-value transactions.
                            </li>
                        </ul>
                    </article>
                </div>
            </section>
        </Layout>
    );
};

export default ScamPrevention;
